import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useLocation,
} from 'react-router';
import {
  getWishlistItems as getWishlistItemsSelector,
  getSelectedWishlistItemsIds as getSelectedItemTypeIdsSelector,
  getOpenedWishlistItem as getOpenedWishlistItemSelector,
  getWishlistItemsLoadingState as getWishlistItemsLoadingStateSelector,
  getWishlistItemsFilters as getWishlistItemsFiltersSelector,
  getSelectedItemTypeNames as getSelectedItemTypeNamesSelector,
  getSelectedItems,
  getQueryArray,
} from 'store/wishlist-items/selectors';
import {
  selectItem as selectItemAction,
  deselectItem as deselectItemAction,
  deselectMany as deselectManyAction,
  resetWishlistItems, resetWishlistItemsActions,
  openWishlistItem as openWishlistItemAction,
  closeWishlistItem as closeWishlistItemAction,
  getWishlistItems as getWishlistItemsAction,
  setWishlistItemsFilters, getWishlistItemsDelayed,
} from 'store/wishlist-items/actions';
import { BatchSelectContainer } from 'library/components/batch-select/styles';
import BatchSelect from 'library/components/batch-select';
import { TYPE } from 'library/constants/models/wishlist-item';
import Loader from 'library/components/entity-loader';
import Filters from 'library/components/filters';
import pluralize from 'library/helpers/pluralize';
import ViewMode from 'library/components/view-mode';
import {
  Redirect, Route, Switch, useRouteMatch,
} from 'react-router-dom';
import { FILTER_KEYS } from 'store/wishlist-items/fixtures';
import ROUTES from 'library/constants/routes';
import withNavigationFilters from 'library/hocs/with-navigation-filters';
import IconNames from 'library/constants/icon-names';
import { resetItem } from 'store/items/actions';
import { batchUpdateItem, resetUpdatedItems } from '../../../store/items/actions';
import NoContent from '../../../library/components/no-content';
import {
  getBrowseItems as getBrowseItemsAction,
  getBrowseItemsDebaunced,
  setBrowseItemsFilters,
} from '../../../store/browse-items/actions';
import { getWishlistItemsDebaunced } from '../../../store/wishlist-items/sagas';

const {
  CATEGORIES,
  MANUFACTURERS,
  TAGS,
  ITEMS,
  STORAGE,
  // HAS_MORE,
} = FILTER_KEYS;

const Wishlist = () => {
  const dispatch = useDispatch();
  const items = useSelector(getWishlistItemsSelector);
  const filters = useSelector(getWishlistItemsFiltersSelector);
  const loading = useSelector(getWishlistItemsLoadingStateSelector);
  const openedItemId = useSelector(getOpenedWishlistItemSelector);
  const selectedDataIds = useSelector(getSelectedItemTypeIdsSelector);
  const selectedItems = useSelector(getSelectedItems);
  const selectedItemTypeNames = useSelector(getSelectedItemTypeNamesSelector);
  const { pathname } = useLocation();
  const showFilter = pathname !== ROUTES.SETTINGS;
  const { path } = useRouteMatch();

  const handleToggleItemSelected = (id) => {
    if (!selectedDataIds.includes(id)) {
      return dispatch(selectItemAction({ id }));
    }

    return dispatch(deselectItemAction({ id }));
  };

  const handleOpenItem = (id) => {
    dispatch(resetWishlistItemsActions());

    dispatch(openWishlistItemAction({ id }));
  };

  const handleCloseItem = () => {
    dispatch(closeWishlistItemAction());
  };

  const handleDeselectAll = (ids) => {
    dispatch(deselectManyAction({ ids }));
  };

  const handleEntitiesReset = () => {
    dispatch(resetWishlistItems());
    dispatch(resetWishlistItemsActions());
  };

  const handleLoadMore = () => {
    dispatch(getWishlistItemsAction());
  };

  const handleFilterChange = (values, isDebaunced = false) => {
    if(isDebaunced) {
      dispatch(getWishlistItemsDelayed({
        values,
        filtersSnapshot: JSON.parse(JSON.stringify(filters)),
      }));
    } else {
      dispatch(setWishlistItemsFilters(values));
      dispatch(getWishlistItemsAction());

      dispatch(deselectManyAction({ ids: selectedDataIds }));
    }
  };

  const onDeselect = (id) => {
    dispatch(deselectManyAction({ ids: [id] }));
  };

  const handleQueryParamDelete = ({ type, value }) => {
    const { [type]: updatedField, ...restFilters } = filters;

    dispatch(setWishlistItemsFilters({
      ...restFilters,
      ...([TAGS, ITEMS].includes(type) ? { [type]: updatedField.filter((item) => item.id !== value) } : {}),
      ...([CATEGORIES, STORAGE].includes(type) ? { [type]: updatedField.filter((itemId) => itemId !== value) } : {}),
      ...(type === MANUFACTURERS ? {
        [type]: updatedField.filter((filter) => {
          return (Array.isArray(value) ? !value.includes(filter) : filter !== value);
        }),
      } : {}),
    }));

    dispatch(deselectManyAction({ ids: selectedDataIds }));
    dispatch(getWishlistItemsAction());
  };

  const handleQueryParamsReset = () => {
    dispatch(setWishlistItemsFilters());
    dispatch(getWishlistItemsAction());

    dispatch(deselectManyAction({ ids: selectedDataIds }));
  };

  const handleRemoveFromWishlistSubmit = () => {
    const data = { is_wishlisted: false };

    const messages = {
      success: `You successfully removed ${pluralize('item', selectedDataIds.length)} from the wishlist.`,
      error: 'Oops :( Remove failed. \nPlease try again.',
    };

    dispatch(batchUpdateItem({
      validIds: selectedDataIds,
      data,
      messages,
      resetWishlist: true,
    }));
  };

  const handleAddPropertiesBatchSubmit = () => (data, type) => {
    const messages = {
      success: `New ${type} successfully set.`,
      error: 'Oops :(\nWe could not apply this {value}.',
    };

    dispatch(batchUpdateItem({
      validIds: selectedDataIds,
      data,
      messages,
      resetWishlist: true,
    }));
  };
  const baseQuery = useSelector(getQueryArray);

  useEffect(() => {
    dispatch(resetUpdatedItems());
  }, []);

  const handleResetItem = () => {
    dispatch(resetItem());
  };

  return (
    <>
      <Filters
        hasEmptyManufacturers
        hasEmptyStorages
        filters={filters}
        isShown={showFilter}
        baseQuery={baseQuery}
        onChange={handleFilterChange}
        onDelete={handleQueryParamDelete}
        onReset={handleQueryParamsReset}
        areInactiveCategoriesIncluded
        multiselectProcessDelayMS={2000}
        isContentDisabledWhenOpen
      />
      <Switch>
        <Route path={`${path}/:mode`} exact>
          <ViewMode
            data={items}
            type={TYPE.ITEM}
            openedItemId={openedItemId}
            handleOpen={handleOpenItem}
            handleToggle={handleToggleItemSelected}
            handleClose={handleCloseItem}
            selectedIds={selectedDataIds}
            handleReset={handleResetItem}
          />
          {items.length === 0 && !loading ? <NoContent title="You have not wishlisted items yet" /> : null}
          <Loader
            isLoading={loading}
            currentAmount={items.length}
            hasMore={filters[FILTER_KEYS.HAS_MORE]}
            onScroll={handleLoadMore}
            onReset={handleEntitiesReset}
          />
          <BatchSelectContainer>
            <BatchSelect
              type={TYPE.ITEM}
              data={selectedItems}
              titles={selectedItemTypeNames}
              onDeselectAll={() => handleDeselectAll(selectedDataIds)}
              onDeselect={onDeselect}
              onDelete={handleRemoveFromWishlistSubmit}
              onBatchAddProperties={handleAddPropertiesBatchSubmit()}
              disabledActions={[IconNames.HEART, IconNames.TAG]}
              deleteType="wishlist-item"
            />
          </BatchSelectContainer>
        </Route>
        <Route path="*">
          <Redirect to={ROUTES.NOT_FOUND} />
        </Route>
      </Switch>
    </>
  );
};

export default withNavigationFilters(Wishlist);
